/**
 * Network-layer enforcement for menu-only links.
 *
 * UI gating alone is a suggestion: hidden buttons and redirected routes stop
 * the accidental path, not a stale bookmark, a restored tab, or a screen that
 * fires a request on mount before its guard renders. This predicate is the
 * backstop, consulted by `apiFetch`.
 *
 * It is deliberately a pure function over (path, method) so the whole policy
 * is testable without a fetch, a component, or a network mock.
 *
 * Note this is a *client-side* backstop only. The online-ordering service does
 * not itself check link type on order creation, so anyone willing to open
 * devtools can still drive an order against a digital-menu link. Closing that
 * needs a server-side check — tracked separately.
 */

/** Everything below only applies to this surface. */
const ONLINE_ORDERING_PREFIX = "/v1/online-ordering";

/**
 * Read-only paths a digital menu genuinely needs. Checked before the
 * account-read deny list so that e.g. `/items/reviews/summary` is not caught
 * by a broader rule later.
 */
const ALWAYS_ALLOWED = [
  "/config/",
  "/menu-schema",
  "/availability/",
  "/reviews/summary",
];

/**
 * Account-scoped reads. A menu-only link has no signed-in customer, so these
 * are all meaningless at best and a data leak through a stale token at worst.
 *
 * `/orders` also covers review submission and eligibility
 * (`/orders/{id}/review*`) — correct, since both require a real order.
 */
const BLOCKED_READ_SEGMENTS = [
  "/customers",
  "/orders",
  "/loyalty",
  "/offers",
  "/addresses",
  "/payment",
  // Earnings (welcome offer) is loyalty for ordering customers.
  "/earnings",
];

/**
 * True when this request must not be made on a menu-only link.
 *
 * Policy:
 *   - any non-GET on the online-ordering surface is blocked (order creation,
 *     payments, OTP, customer create, address writes, group cart, split pay,
 *     push registration);
 *   - GETs are blocked only when account-scoped;
 *   - everything off the online-ordering surface is left alone.
 */
export function isBlockedInMenuOnly(
  path: string,
  method: string | undefined,
): boolean {
  if (!path.includes(ONLINE_ORDERING_PREFIX)) return false;

  const verb = (method ?? "GET").toUpperCase();
  // Strip the query string so `?ids=/orders` style params can't smuggle a
  // segment match, and so allow-list checks see a clean path.
  const [pathname] = path.split("?");

  if (ALWAYS_ALLOWED.some((allowed) => pathname.includes(allowed))) {
    // Allowed for reads only — nothing on this list has a legitimate write.
    return verb !== "GET";
  }

  if (verb !== "GET") return true;

  return BLOCKED_READ_SEGMENTS.some((segment) => pathname.includes(segment));
}

/**
 * Raised instead of letting a blocked call reach the network.
 *
 * Deliberately not an auth error: reaching this means UI gating has a hole, so
 * it should read as a bug in the build rather than a session problem. `apiFetch`
 * routes it away from the 401 path so it can't trip the sign-in modal or the
 * token-refresh retry.
 */
export class MenuOnlyModeError extends Error {
  readonly path: string;
  readonly method: string;

  constructor(path: string, method: string | undefined) {
    super(
      `Blocked ${method ?? "GET"} ${path}: this link is a digital menu and cannot order.`,
    );
    this.name = "MenuOnlyModeError";
    this.path = path;
    this.method = method ?? "GET";
  }
}
